import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Brain,
  Bot,
  GitGraph,
  Database,
  Code,
  ChevronRight,
  CheckCircle,
  Cpu,
  Network,
  BarChart,
  MessageSquare,
  FileText,
  Lightbulb,
  ArrowRight,
  Globe,
  Mic,
  Play,
  Languages,
  Building2,
  Factory,
  Film,
  Users,
  Clock,
  TrendingUp,
  Shield,
  Award,
  Zap
} from "lucide-react";

const SolutionsPage = () => {
  // Featured Solutions based on real implementations
  const featuredSolutions = [
    {
      id: "translation-platform",
      title: "Enterprise Real-Time Translation Platform",
      subtitle: "Breaking Language Barriers in Professional Services",
      description: "A comprehensive real-time translation solution enabling seamless multilingual communication for financial advisors, healthcare providers, and professional services.",
      icon: <Globe className="w-8 h-8 text-white" />,
      bgGradient: "from-blue-600 to-cyan-600",
      features: [
        "Real-time speech-to-text in 20+ languages",
        "Instant bi-directional translation",
        "Automated session transcripts and PDFs",
        "Compliance-ready recording and storage",
        "Video conferencing integration"
      ],
      metrics: {
        languages: "20+",
        accuracy: "98%",
        latency: "<500ms",
        users: "10,000+"
      },
      useCases: [
        "Financial advisory across languages",
        "Healthcare consultations",
        "Legal services",
        "Customer support"
      ],
      technologies: ["Azure Cognitive Services", "WebRTC", "AWS Lambda", "Real-time APIs"]
    },
    {
      id: "industrial-assistant",
      title: "Industrial AI Knowledge Assistant",
      subtitle: "Intelligent Document Processing for Manufacturing",
      description: "Custom AI assistant that processes technical documentation, manuals, and specifications to provide instant, accurate answers for industrial operations.",
      icon: <Factory className="w-8 h-8 text-white" />,
      bgGradient: "from-purple-600 to-indigo-600",
      features: [
        "Multi-format document processing (PDF, Word, Excel)",
        "Technical specification extraction",
        "RAG-powered knowledge retrieval",
        "Predictive maintenance insights",
        "Multi-language technical support"
      ],
      metrics: {
        documents: "10TB+",
        accuracy: "99.2%",
        response: "<1 sec",
        queries: "1M+/month"
      },
      useCases: [
        "Equipment troubleshooting",
        "Maintenance procedures",
        "Safety compliance",
        "Training and onboarding"
      ],
      technologies: ["Claude 3", "RAG Architecture", "Vector DBs", "OCR", "LangChain"]
    },
    {
      id: "video-translation",
      title: "AI Video Translation & Localization Suite",
      subtitle: "Global Content at Native Quality",
      description: "Revolutionary platform that translates videos into 20 languages with voice cloning and lip-sync technology, making content appear natively produced.",
      icon: <Film className="w-8 h-8 text-white" />,
      bgGradient: "from-green-600 to-emerald-600",
      features: [
        "20-language video translation",
        "Voice cloning technology",
        "AI-powered lip synchronization",
        "Subtitle generation and timing",
        "Batch processing capabilities"
      ],
      metrics: {
        languages: "20",
        processing: "10x faster",
        quality: "95% native",
        cost: "80% reduction"
      },
      useCases: [
        "Corporate training videos",
        "Marketing content",
        "Educational materials",
        "Entertainment media"
      ],
      technologies: ["Deep Learning", "Voice Synthesis", "Computer Vision", "Neural Networks"]
    }
  ];

  // Solution categories for navigation
  const solutionCategories = [
    {
      id: "gen-ai",
      title: "Generative AI Solutions",
      description: "Custom LLMs, content generation, and intelligent automation",
      icon: <Brain className="w-6 h-6" />,
      link: "/solution/gen-ai-solutions",
      color: "blue"
    },
    {
      id: "ai-agents",
      title: "AI Agents & Automation",
      description: "Autonomous agents for workflow automation and task execution",
      icon: <Bot className="w-6 h-6" />,
      link: "/solution/ai-agents-solutions",
      color: "purple"
    },
    {
      id: "custom-ai",
      title: "Custom AI Development",
      description: "Industry-specific AI solutions tailored to your needs",
      icon: <Code className="w-6 h-6" />,
      link: "/solution/custom-ai-solutions",
      color: "indigo"
    }
  ];

  // Industries served
  const industries = [
    { name: "Financial Services", icon: <Building2 className="w-6 h-6" />, solutions: 25 },
    { name: "Healthcare", icon: <Users className="w-6 h-6" />, solutions: 18 },
    { name: "Manufacturing", icon: <Factory className="w-6 h-6" />, solutions: 32 },
    { name: "Media & Entertainment", icon: <Film className="w-6 h-6" />, solutions: 15 }
  ];

  // Process steps
  const processSteps = [
    {
      title: "Discovery Workshop",
      description: "Deep dive into your challenges, requirements, and objectives",
      duration: "1-2 days",
      icon: <Lightbulb className="w-6 h-6" />
    },
    {
      title: "Solution Architecture",
      description: "Design custom AI architecture tailored to your infrastructure",
      duration: "1 week",
      icon: <GitGraph className="w-6 h-6" />
    },
    {
      title: "Rapid Prototyping",
      description: "Build and test proof of concept with real data",
      duration: "2-3 weeks",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Production Deployment",
      description: "Scale, optimize, and deploy with enterprise security",
      duration: "4-8 weeks",
      icon: <Shield className="w-6 h-6" />
    }
  ];

  return (
    <div className="bg-gray-50">
      <Helmet>
        <title>Enterprise AI Solutions - Real Implementations | AI Guru</title>
        <meta 
          name="description" 
          content="Explore our proven AI solutions: real-time translation platforms, industrial AI assistants, video localization with voice cloning. See how we've transformed businesses across industries." 
        />
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-darkgray via-deepblue to-darkgray text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-blue-900/30 backdrop-blur-sm border border-blue-500/20 rounded-full px-6 py-2 mb-6">
              <Award className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-300">100+ Successful Implementations</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Proven AI Solutions
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mt-2">
                Delivering Real Business Impact
              </span>
            </h1>
            <p className="mt-6 text-xl text-gray-300 max-w-3xl mx-auto">
              From concept to production in weeks. Explore how our custom AI solutions have transformed 
              operations for industry leaders across the globe.
            </p>
          </div>
        </div>
      </div>

      {/* Featured Solutions Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Featured Enterprise Solutions
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Real implementations delivering transformative results
            </p>
          </div>

          <div className="space-y-20">
            {featuredSolutions.map((solution, index) => (
              <div key={solution.id} className={`flex flex-col lg:flex-row gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Solution Details */}
                <div className="flex-1 space-y-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${solution.bgGradient}`}>
                    {solution.icon}
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900">{solution.title}</h3>
                    <p className="text-xl text-gray-600 mt-2">{solution.subtitle}</p>
                  </div>
                  <p className="text-lg text-gray-700">{solution.description}</p>
                  
                  {/* Key Features */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900">Key Features:</h4>
                    <ul className="space-y-2">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6">
                    {Object.entries(solution.metrics).map(([key, value]) => (
                      <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{value}</div>
                        <div className="text-sm text-gray-600 capitalize">{key}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Use Cases Card */}
                <div className="flex-1 lg:max-w-md">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-xl border border-gray-100">
                    <h4 className="text-xl font-bold text-gray-900 mb-6">Industry Applications</h4>
                    <div className="space-y-4">
                      {solution.useCases.map((useCase, idx) => (
                        <div key={idx} className="flex items-center">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                          <span className="text-gray-700">{useCase}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-gray-200">
                      <h5 className="font-semibold text-gray-900 mb-3">Technologies Used:</h5>
                      <div className="flex flex-wrap gap-2">
                        {solution.technologies.map((tech, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Solution Categories Navigation */}
      <div className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Explore All Solution Categories
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Comprehensive AI solutions for every business challenge
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutionCategories.map((category) => (
              <Link 
                key={category.id}
                to={category.link}
                className="group bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-14 h-14 bg-${category.color}-100 rounded-xl flex items-center justify-center text-${category.color}-600 mb-6 group-hover:scale-110 transition-transform`}>
                  {category.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{category.title}</h3>
                <p className="text-gray-600 mb-4">{category.description}</p>
                <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                  Learn more
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Industries Served */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Industries We Transform
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Proven expertise across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center text-gray-600 mx-auto mb-4">
                  {industry.icon}
                </div>
                <h3 className="font-bold text-gray-900">{industry.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{industry.solutions} solutions deployed</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Implementation Process */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900">
              From Concept to Production
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
              Our proven process ensures rapid deployment with minimal risk
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-6 rounded-xl shadow-lg h-full">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <div className="text-sm font-medium text-blue-600">{step.duration}</div>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Metrics */}
      <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold">
              Delivering Measurable Results
            </h2>
            <p className="mt-4 text-xl text-blue-100">
              Our solutions drive real business impact
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">85%</div>
              <div className="text-blue-100">Average efficiency gain</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">3.2x</div>
              <div className="text-blue-100">ROI within 12 months</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">60%</div>
              <div className="text-blue-100">Cost reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-blue-100">Client satisfaction</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-6">
            Ready to Build Your Custom AI Solution?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can transform your business with AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contactUs" 
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a 
              href="#featured-solutions" 
              className="inline-flex items-center px-8 py-4 border border-gray-300 text-lg font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Download Case Studies
              <FileText className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionsPage;
